import React, { ReactNode, useRef } from 'react';

import { Box, Drawer, DrawerProps, IconButton, Stack, Typography, useTheme } from '@mui/material';

import ScrollShade from '~components/containers/scrollShade';
import VectorImage from '~components/media/vectorImage';
import { useScrollShade } from '~hooks';
import { Dimens } from '~themes';

interface CloseableDrawerProps extends DrawerProps {
	/**
	 * Title of the drawer
	 */
	title: string;
	/**
	 * Description of the drawer.
	 */
	description?: string;
	headerContent?: ReactNode;
	footerContent?: ReactNode;
}

const CloseableDrawer = ({
	description,
	headerContent,
	footerContent,
	...props
}: CloseableDrawerProps) => {
	const { children, title, ...rest } = props;
	const { palette } = useTheme();
	const contentRef = useRef<HTMLDivElement>(null);
	const shade = useScrollShade(contentRef);

	const handleCloseClick = (e: React.MouseEvent) => {
		if (props.onClose) {
			// TODO: override the model reason and add something like 'closeMouseClick'?
			// Set for now on escapeKeyDown because we like to ignore backdropClick
			props.onClose(e, 'escapeKeyDown');
		}
	};

	const showDescription = (description?.length ?? 0) > 0;
	return (
		<Drawer {...rest}>
			<Stack
				direction='column'
				gap={Dimens.spacingXl}
				sx={{
					height: '100%',
					minWidth: Dimens.fixed(320),
					marginX: Dimens.spacingLg,
					gap: Dimens.spacingXl,
				}}
			>
				<Stack
					sx={{ width: '100%', flex: '0 0 auto', mt: Dimens.spacingXl }}
					gap={Dimens.spacingXl}
				>
					<Stack direction='row'>
						<Stack justifyItems='start' sx={{ flex: 1 }}>
							<Typography variant='headingXs'>{title}</Typography>
							{showDescription && <Typography variant='paragraph'>{description}</Typography>}
						</Stack>
						<IconButton onClick={handleCloseClick}>
							<VectorImage name='icon/cross' />
						</IconButton>
					</Stack>
					{headerContent}
				</Stack>

				<Box
					ref={contentRef}
					sx={{
						width: '100%',
						flex: '1 1 auto',
						minHeight: 0,
						overflowY: 'auto',
						scrollMargin: '12px',
						'&::-webkit-scrollbar': { display: 'none' },
						msOverflowStyle: 'none',
						scrollbarWidth: 'none',
						scrollbarColor: 'transparent transparent',
						scrollbarGutter: 'stable',
					}}
				>
					<ScrollShade edge='top' visible={shade.top} surface={palette.background?.paper} />
					{children}
					<ScrollShade edge='bottom' visible={shade.bottom} surface={palette.background?.paper} />
				</Box>

				<Box sx={{ width: '100%', flex: '0 0 auto', mb: Dimens.spacingXl }}>{footerContent}</Box>
			</Stack>
		</Drawer>
	);
};

export type { CloseableDrawerProps };

export default CloseableDrawer;
